import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { verifyEmail, resendVerification } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (code.length !== 6) {
      setError(
        t('verifyEmail.errors.enterSixDigitCode', 'Please enter a 6-digit verification code'),
      );
      setIsLoading(false);
      return;
    }

    try {
      await verifyEmail(email, code);
      setSuccess(true);
      setTimeout(() => {
        navigate('/companion/name');
      }, 2000);
    } catch (err: any) {
      setError(err.message || t('verifyEmail.errors.verificationFailed', 'Verification failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      await resendVerification(email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || t('verifyEmail.errors.failedToResend', 'Failed to resend code'));
    } finally {
      setResendLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
        <div className="flex-1 flex flex-col max-w-mobile md:max-w-desktop mx-auto w-full">
          <div className="flex-1 flex flex-col justify-center px-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-3xl bg-green-500/20 mb-4">
                <svg
                  className="h-10 w-10 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text">
                {t('verifyEmail.emailVerified', 'Email Verified!')}
              </h2>
              <p className="mt-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t('verifyEmail.settingUpCompanion', 'Setting up your companion...')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      {/* Mobile container */}
      <div className="flex-1 flex flex-col max-w-mobile md:max-w-desktop mx-auto w-full">
        {/* Header */}
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/signup')}
            className="p-2 hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col justify-center px-6 pb-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-primary-500 rounded-3xl mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl">✉️</span>
            </div>
            <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
              {t('verifyEmail.title', 'Verify your email')}
            </h1>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
              {t('verifyEmail.sentCodeTo', "We've sent a verification code to")}
            </p>
            <p className="text-primary-500 font-medium">{email}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {resendSuccess && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                <p className="text-green-400 text-sm text-center">
                  {t('verifyEmail.codeSentCheckEmail', 'Verification code sent! Check your email.')}
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('verifyEmail.verificationCode', 'Verification Code')}
              </label>
              <input
                id="code"
                name="code"
                type="text"
                maxLength={6}
                pattern="[0-9]{6}"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-2xl tracking-widest"
                placeholder={t('verifyEmail.codePlaceholder', '000000')}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('verifyEmail.verifying', 'Verifying...')
                : t('verifyEmail.verifyEmailButton', 'Verify Email')}
            </button>
          </form>

          {/* Footer links */}
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading}
              className="text-sm text-primary-500 hover:text-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendLoading
                ? t('verifyEmail.sending', 'Sending...')
                : t('verifyEmail.didntReceiveCode', "Didn't receive a code? Resend")}
            </button>
            <Link
              to="/signup"
              className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-dark-text dark:hover:text-dark-text hover:text-light-text transition-colors"
            >
              {t('verifyEmail.backToSignup', 'Back to signup')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
